import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, Star } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { BusinessUnitDialog } from './business-unit-dialog';
import { CloneProdDialog } from './clone-prod-dialog';
import { IS_DEV_STACK } from '@/lib/api';
import { InboxesSubsection } from './inboxes-subsection';
import { UnassignedInboxesSection } from './unassigned-inboxes-section';
import {
  useBusinessUnits,
  useBusinessUnitMutations
} from '@/hooks/use-business-units';
import { useInboxes } from '@/hooks/use-inboxes';
import { belongsToUnit } from '../inbox-units';
import type { BusinessUnit } from '@/types';

export function BusinessUnitsSection() {
  const { data: units = [], isLoading, error } = useBusinessUnits();
  const { updateBusinessUnit } = useBusinessUnitMutations();
  const { data: inboxes = [] } = useInboxes();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Child rows mount only while expanded, so an account with many units does not fan
  // out a request per unit on first paint.
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Counts the unit as home whether it is the inbox's primary or one of its extras — a
  // shared inbox is not "missing" from the units it only holds a share of.
  const inboxCount = (buId: number) =>
    inboxes.filter((i) => belongsToUnit(i, buId)).length;

  const patch = async (
    bu: BusinessUnit,
    payload: Parameters<typeof updateBusinessUnit>[1],
    okMessage: string
  ) => {
    setBusyId(bu.id);
    try {
      await updateBusinessUnit(bu.id, payload);
      toast.success(okMessage);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-start justify-between gap-4'>
        <p className='max-w-xl text-sm text-muted-foreground'>
          Business units are the brands inside this account. Every account has
          one default unit (created automatically with the account&apos;s
          name); data without a business unit belongs to the account as a
          whole.
        </p>
        <div className='flex shrink-0 items-center gap-3'>
          {IS_DEV_STACK && (
            <CloneProdDialog
              trigger={
                <Button size='sm' variant='outline'>
                  Clone from production
                </Button>
              }
            />
          )}
          <BusinessUnitDialog trigger={<Button size='sm'>Add business unit</Button>} />
        </div>
      </div>

      <div className='overflow-hidden rounded-xl border bg-card shadow-sm'>
        <Table>
          <TableHeader className='bg-muted/40'>
            <TableRow className='hover:bg-transparent'>
              <TableHead className='h-11 w-10 pl-4' />
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Inboxes</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className='pr-4 text-right'>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Skeleton className='h-5 w-full' />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className='py-8 text-center text-sm text-destructive'
                >
                  {error instanceof Error
                    ? error.message
                    : 'Failed to load business units.'}
                </TableCell>
              </TableRow>
            ) : units.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className='py-8 text-center text-sm text-muted-foreground'
                >
                  No business units yet.
                </TableCell>
              </TableRow>
            ) : (
              units.map((bu) => {
                const busy = busyId === bu.id;
                const inactive = bu.status === 'inactive';
                const isOpen = expanded.has(bu.id);
                const count = inboxCount(bu.id);
                return (
                  <Fragment key={bu.id}>
                  <TableRow>
                    <TableCell className='w-10 pl-4'>
                      <button
                        type='button'
                        onClick={() => toggle(bu.id)}
                        aria-expanded={isOpen}
                        aria-label={
                          isOpen
                            ? `Hide inboxes for ${bu.name}`
                            : `Show inboxes for ${bu.name}`
                        }
                        className='text-muted-foreground hover:text-foreground'
                      >
                        {isOpen ? (
                          <ChevronDown className='h-4 w-4' />
                        ) : (
                          <ChevronRight className='h-4 w-4' />
                        )}
                      </button>
                    </TableCell>
                    <TableCell className='font-medium'>
                      <span className='inline-flex items-center gap-1.5'>
                        {bu.is_default && (
                          <Star
                            className='h-3.5 w-3.5 fill-current text-amber-500'
                            role='img'
                            aria-label='Default business unit'
                          />
                        )}
                        {bu.name}
                      </span>
                    </TableCell>
                    <TableCell className='font-mono text-xs text-muted-foreground'>
                      {bu.slug}
                    </TableCell>
                    <TableCell className='font-mono text-xs text-muted-foreground'>
                      {bu.id}
                    </TableCell>
                    <TableCell>
                      <button
                        type='button'
                        onClick={() => toggle(bu.id)}
                        className='text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline'
                      >
                        {count} inbox{count === 1 ? '' : 'es'}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant={inactive ? 'outline' : 'secondary'}>
                        {inactive ? 'Inactive' : 'Active'}
                      </Badge>
                    </TableCell>
                    <TableCell className='space-x-1 pr-4 text-right'>
                      <BusinessUnitDialog
                        businessUnit={bu}
                        trigger={
                          <Button variant='ghost' size='sm' disabled={busy}>
                            Edit
                          </Button>
                        }
                      />
                      {!bu.is_default && !inactive && (
                        <Button
                          variant='ghost'
                          size='sm'
                          disabled={busy}
                          onClick={() =>
                            patch(bu, { is_default: true }, `${bu.name} is now the default`)
                          }
                        >
                          Make default
                        </Button>
                      )}
                      {/* Deactivate is gone from this panel. ACTIVATE STAYS: a unit that is
                          already inactive would otherwise be stranded with no way back, and
                          machine still accepts the status either way. */}
                      {inactive && (
                        <Button
                          variant='ghost'
                          size='sm'
                          disabled={busy}
                          onClick={() =>
                            patch(bu, { status: 'active' }, 'Business unit activated')
                          }
                        >
                          Activate
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className='hover:bg-transparent'>
                      <TableCell colSpan={7} className='bg-muted/20 p-4'>
                        <InboxesSubsection businessUnit={bu} />
                      </TableCell>
                    </TableRow>
                  )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <UnassignedInboxesSection />
    </div>
  );
}
